import { IconQuestionMarkOutline } from '@iwoca/icons';
import { GetDrawDownRepaymentScheduleCreditFacilityResponse } from '@iwoca/lapi-client/edge';
import { Accordion } from '@iwoca/orion';
import { Divider, H3, Text } from '@ui';
import { FormattedMessage } from 'react-intl';

import { Rate } from '.././../../../../myAccount/ui/rate';
import { MoneyNoDecimals } from '../../../../../myAccount/ui/moneyNoDecimals';

export const BlendedRateSummary = ({
    topupDrawdownAmount,
    blendedRateDetails
}: {
    topupDrawdownAmount: number;
    blendedRateDetails: GetDrawDownRepaymentScheduleCreditFacilityResponse['data']['summary']['blended_rate_details'];
}) => {
    if (!blendedRateDetails) return null;

    const {
        principal_pre_funding,
        rate_on_pre_funding_principal,
        rate_on_post_funding_principal,
        principal_post_funding,
        rate_on_funding_amount
    } = blendedRateDetails;

    if (
        rate_on_pre_funding_principal === rate_on_funding_amount ||
        rate_on_funding_amount === rate_on_post_funding_principal
    )
        return null;

    return (
        <div data-testid="blendedRateSummary">
            <H3>
                <FormattedMessage
                    id="_.outstanding_view.dashboardActionPanel.blended_rate_summary.title"
                    defaultMessage="Your blended rate"
                />
            </H3>
            <Text className="my-s">
                <FormattedMessage
                    id="_.outstanding_view.dashboardActionPanel.blended_rate_summary.description"
                    defaultMessage="The rate on your existing principal won't change - we'll combine this with any new funds into a single loan and calculate a blended rate for you, as outlined below."
                />
            </Text>
            <Accordion
                title={
                    <FormattedMessage
                        id="_.outstanding_view.dashboardActionPanel.blended_rate_summary.accordion_title"
                        defaultMessage="How is this calculated"
                        key="descriptionTitle" // Required as otherwise react moans about not having a key. This is weird behaviour when combining Accordion and FormattedMessage
                    />
                }
                icon={<IconQuestionMarkOutline />}
                small
                className="mb-l box-border w-full min-m:w-3/4"
            >
                <Text>
                    <FormattedMessage
                        id="_.outstanding_view.dashboardActionPanel.blended_rate_summary.accordion_description"
                        defaultMessage="We calculate your blended rate using the follow steps:"
                    />
                </Text>

                <ol className="space-y-l pl-l">
                    <li>
                        <FormattedMessage
                            id="_.outstanding_view.dashboardActionPanel.blended_rate_summary.description_list_item_one"
                            defaultMessage="Multiply your existing principal by your current rate:"
                        />
                        <div className="font-bold">
                            <FormattedMessage
                                id="_.outstanding_view.dashboardActionPanel.blended_rate_summary.formula_list_item_one"
                                defaultMessage="{principal_pre_funding} × {rate_on_pre_funding_principal}"
                                values={{
                                    principal_pre_funding: (
                                        <MoneyNoDecimals
                                            value={principal_pre_funding}
                                        />
                                    ),

                                    rate_on_pre_funding_principal: (
                                        <Rate
                                            value={
                                                rate_on_pre_funding_principal
                                            }
                                        />
                                    )
                                }}
                            />
                        </div>
                    </li>
                    <li>
                        <FormattedMessage
                            id="_.outstanding_view.dashboardActionPanel.blended_rate_summary.description_list_item_two"
                            defaultMessage="Multiply your new funds by your new rate, and add this to the amount above:"
                        />
                        <div className="font-bold">
                            <FormattedMessage
                                id="_.outstanding_view.dashboardActionPanel.blended_rate_summary.formula_list_item_two"
                                defaultMessage="({newFundingAmount} × {rate_on_funding_amount}) + ({principal_pre_funding} × {rate_on_pre_funding_principal})"
                                values={{
                                    principal_pre_funding: (
                                        <MoneyNoDecimals
                                            value={principal_pre_funding}
                                        />
                                    ),
                                    rate_on_pre_funding_principal: (
                                        <Rate
                                            value={
                                                rate_on_pre_funding_principal
                                            }
                                        />
                                    ),
                                    newFundingAmount: (
                                        <MoneyNoDecimals
                                            value={topupDrawdownAmount}
                                        />
                                    ),
                                    rate_on_funding_amount: (
                                        <Rate value={rate_on_funding_amount} />
                                    )
                                }}
                            />
                        </div>
                    </li>
                    <li>
                        <FormattedMessage
                            id="_.outstanding_view.dashboardActionPanel.blended_rate_summary.description_list_item_three"
                            defaultMessage="Divide this by your new outstanding balance:"
                        />
                        <div className="font-bold">
                            <FormattedMessage
                                id="_.outstanding_view.dashboardActionPanel.blended_rate_summary.formula_list_item_three"
                                defaultMessage="(({newFundingAmount} × {rate_on_funding_amount}) + ({principal_pre_funding} × {rate_on_pre_funding_principal})) ÷ ({principal_pre_funding} + {newFundingAmount})"
                                values={{
                                    principal_pre_funding: (
                                        <MoneyNoDecimals
                                            value={principal_pre_funding}
                                        />
                                    ),
                                    rate_on_pre_funding_principal: (
                                        <Rate
                                            value={
                                                rate_on_pre_funding_principal
                                            }
                                        />
                                    ),
                                    newFundingAmount: (
                                        <MoneyNoDecimals
                                            value={topupDrawdownAmount}
                                        />
                                    ),
                                    rate_on_funding_amount: (
                                        <Rate value={rate_on_funding_amount} />
                                    )
                                }}
                            />
                        </div>
                    </li>
                </ol>
            </Accordion>
            <div className="space-y-l">
                <div className="flex items-center justify-between">
                    <FormattedMessage
                        id="_.outstanding_view.dashboardActionPanel.blended_rate_summary.existing_principal"
                        defaultMessage="Existing principal"
                    />
                    <div>
                        <MoneyNoDecimals value={principal_pre_funding} />{' '}
                        <span className="font-reg">
                            <FormattedMessage
                                id="_.outstanding_view.dashboardActionPanel.blended_rate_summary.at"
                                defaultMessage="at"
                            />
                        </span>{' '}
                        <Rate value={rate_on_pre_funding_principal} />
                    </div>
                </div>

                <div className="flex items-center justify-between">
                    <FormattedMessage
                        id="_.outstanding_view.dashboardActionPanel.blended_rate_summary.new_funds"
                        defaultMessage="New funds"
                    />
                    <div>
                        <MoneyNoDecimals value={topupDrawdownAmount} />{' '}
                        <span className="font-reg">
                            <FormattedMessage
                                id="_.outstanding_view.dashboardActionPanel.blended_rate_summary.at"
                                defaultMessage="at"
                            />
                        </span>{' '}
                        <Rate value={rate_on_funding_amount} />
                    </div>
                </div>

                <Divider />

                <div className="flex items-center justify-between">
                    <FormattedMessage
                        id="_.outstanding_view.dashboardActionPanel.blended_rate_summary.new_loan_principal"
                        defaultMessage="New loan principal"
                    />
                    <div>
                        <MoneyNoDecimals value={principal_post_funding} />{' '}
                        <span className="font-reg">
                            <FormattedMessage
                                id="_.outstanding_view.dashboardActionPanel.blended_rate_summary.at"
                                defaultMessage="at"
                            />
                        </span>{' '}
                        <Rate value={rate_on_post_funding_principal} />
                    </div>
                </div>

                <Text className="text-structure-40">
                    <FormattedMessage
                        id="_.outstanding_view.dashboardActionPanel.blended_rate_summary.replacementText"
                        defaultMessage="This will replace your existing loan"
                    />
                </Text>
            </div>
        </div>
    );
};
